<template>
  <div class="announcement-management">
    <div class="header">
      <h2>网站公告管理</h2>
      <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新建公告</el-button>
    </div>

    <el-table :data="announcements" v-loading="loading" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="80"></el-table-column>
      <el-table-column prop="title" label="公告标题" min-width="200"></el-table-column>
      <el-table-column prop="isEnabled" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.isEnabled ? 'success' : 'info'">
            {{ scope.row.isEnabled ? '启用中' : '已禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="updatedAt" label="最后更新" width="160">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.updatedAt) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="250" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button size="mini" :type="scope.row.isEnabled ? 'info' : 'success'" 
            @click="toggleStatus(scope.row)">
            {{ scope.row.isEnabled ? '禁用' : '启用' }}
          </el-button>
          <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="800px" @opened="initEditor" @closed="destroyEditor">
      <el-form :model="form" :rules="rules" ref="form" label-width="80px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入公告标题"></el-input>
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <div ref="editor" class="editor-container"></div>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.isEnabled"></el-switch>
          <span class="tip">（启用后将作为首页唯一显示的弹窗公告）</span>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import E from 'wangeditor'

export default {
  name: 'AnnouncementManagement',
  data() {
    return {
      announcements: [],
      loading: false,
      submitting: false,
      dialogVisible: false,
      dialogTitle: '新建公告',
      editor: null,
      form: {
        id: null,
        title: '',
        content: '',
        isEnabled: true
      },
      rules: {
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.loadAnnouncements()
  },
  methods: {
    async loadAnnouncements() {
      this.loading = true
      try {
        const response = await this.$http.get('/admin/announcements')
        if (response.success) {
          this.announcements = response.data || []
        }
      } catch (error) {
        this.$message.error('加载公告列表失败')
      } finally {
        this.loading = false
      }
    },

    initEditor() {
      if (!this.editor) {
        this.editor = new E(this.$refs.editor)
        
        // 配置上传图片
        this.editor.config.uploadImgShowBase64 = true // 暂时用base64，如果需要上传文件可以配置server
        
        this.editor.create()
      }
      this.editor.txt.html(this.form.content)
    },

    destroyEditor() {
      if (this.editor) {
        // wangeditor 4 不推荐销毁，直接置空内容即可，或者按需处理
        this.editor.txt.clear()
      }
    },

    handleAdd() {
      this.dialogTitle = '新建公告'
      this.form = { id: null, title: '', content: '', isEnabled: true }
      this.dialogVisible = true
    },

    handleEdit(row) {
      this.dialogTitle = '编辑公告'
      this.form = { ...row }
      this.dialogVisible = true
    },

    async toggleStatus(row) {
      try {
        const response = await this.$http.put(`/admin/announcement/${row.id}/status`, null, {
          params: { enabled: !row.isEnabled }
        })
        if (response.success) {
          this.$message.success('操作成功')
          this.loadAnnouncements()
        }
      } catch (error) {
        this.$message.error('操作失败')
      }
    },

    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除此公告吗？', '提示', { type: 'warning' })
        const response = await this.$http.delete(`/admin/announcement/${row.id}`)
        if (response.success) {
          this.$message.success('删除成功')
          this.loadAnnouncements()
        }
      } catch (error) {
        if (error !== 'cancel') this.$message.error('删除失败')
      }
    },

    async submitForm() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return
        
        const content = this.editor.txt.html()
        if (!content || content === '<p><br></p>') {
          this.$message.warning('请输入公告内容')
          return
        }

        this.submitting = true
        try {
          const submitData = { ...this.form, content }
          const response = await this.$http.post('/admin/announcement', submitData)
          if (response.success) {
            this.$message.success('保存成功')
            this.dialogVisible = false
            this.loadAnnouncements()
          }
        } catch (error) {
          this.$message.error('保存失败')
        } finally {
          this.submitting = false
        }
      })
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      return new Date(dateTime).toLocaleString()
    }
  }
}
</script>

<style scoped lang="scss">
.announcement-management {
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h2 { margin: 0; }
  }

  .editor-container {
    border: 1px solid #ccc;
    z-index: 100;
  }
  
  .tip {
    font-size: 12px;
    color: #999;
    margin-left: 10px;
  }
}
</style>
